import React, { useEffect, useId, useState } from 'react';
import PropTypes from 'prop-types';
import { doc, updateDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuLock, LuTriangleAlert, LuUserPen } from 'react-icons/lu';
import { db } from '../../config/firebaseConfig';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useTranslation } from '../../i18n/LanguageContext';
import { EMPLOYEE_ROLE_OPTIONS } from '../../constants/employeeGroups';
import {
  PRIMARY_ADDRESS_HOME,
  PRIMARY_ADDRESS_WORK,
  isPrimaryAddress,
} from '../../utils/employeeAddress';
import { PROFESSION_OPTIONS, parseLanguageInfo, formatLanguageInfo } from '../../constants/employeeOptions';
import { PROVINCES_SORTED } from '../../constants/provinces';
import Badge from '../../components/ui/Badge';
import GlassIcon from '../../components/ui/GlassIcon';
import SegmentedControl from '../../components/ui/SegmentedControl';
// Only the global variables of this stylesheet are still in use (by other
// legacy dialogs); importing it keeps its place in the cascade.
import './EditEmployeeModal.module.css';
import { EMPLOYEE_COLUMNS, isYes, optionLabel } from './employeeColumns';
import EmployeeFormShell from './employeeForm/EmployeeFormShell';
import { CompanyEmailField, Field, SelectInput, TextArea, TextInput, ToggleTile } from './employeeForm/FormControls';
import LanguageEditor from './employeeForm/LanguageEditor';
import useEmployeeFormOptions from './employeeForm/useEmployeeFormOptions';
import { getFormSections, summarizeErrors } from './employeeForm/formSections';
import { NATIONALITY_OPTIONS, isValidLocalPart, isCompanyEmail, localPartOf } from './employeeForm/employeeFormOptions';

// Yetkilendirme alanını yalnızca bu kullanıcı düzenleyebilir
const AUTHORIZATION_EDITOR_EMAIL = 'turanemre1999@gmail.com';

const FLAG_FIELDS = ['isHandicappedPerson', 'isRetired', 'isIntern'];

const asOptions = (values) => values.map((value) => ({ value, label: value }));

/** "Çalışanı Düzenle": the employee form, in sections, filled with the record. */
const EditEmployeeModal = ({ selectedEmployee, onClose, onUpdate }) => {
  const { t } = useTranslation();
  const { userEmail, userAuthorization } = useUserProfile();
  const idPrefix = useId();
  const fieldId = (name) => `${idPrefix}-${name}`;
  const { departments, authorizationRoles, isLoadingDepartments, isLoadingAuthorizationRoles } = useEmployeeFormOptions();

  // Yetkilendirme alanını düzenleyebilecek kullanıcılar:
  // 1. Super admin (turanemre1999@gmail.com)
  // 2. admin rolüne sahip olanlar
  // 3. Kısıtlı Admin rolüne sahip olanlar
  const canEditAuthorization =
    String(userEmail || '').trim().toLowerCase() === AUTHORIZATION_EDITOR_EMAIL ||
    (userAuthorization && (
      userAuthorization.includes('admin') ||
      userAuthorization.includes('Kısıtlı Admin')
    ));

  const [formData, setFormData] = useState({
    documentId: selectedEmployee.id,
    eName: selectedEmployee.eName || '',
    eSurname: selectedEmployee.eSurname || '',
    eMail: selectedEmployee.eMail || '',
    phone: selectedEmployee.phone || '',
    emergencyPhone: selectedEmployee.emergencyPhone || '', // Acil durum telefonu
    address: selectedEmployee.address || '', // Ev adresi
    workAddress: selectedEmployee.workAddress || '',
    workCity: selectedEmployee.workCity || '', // Çalıştığı İl (boş kalabilir)
    departman: selectedEmployee.departman || '',
    authorization: selectedEmployee.authorization || '',
    workType: selectedEmployee.workType || '',
    Active: selectedEmployee.Active !== undefined ? selectedEmployee.Active : 1,
    ID: selectedEmployee.ID || '',
    annualPermit: selectedEmployee.annualPermit || '',
    birthday: selectedEmployee.birthday || '',
    isHandicappedPerson: selectedEmployee.isHandicappedPerson || 'No',
    isIntern: selectedEmployee.isIntern || 'No',
    isRetired: selectedEmployee.isRetired || 'No',
    languageInfo: selectedEmployee.languageInfo || '',
    nationality: selectedEmployee.nationality || '',
    profession: selectedEmployee.profession || '',
    educationInfo: selectedEmployee.educationInfo || '',
    startDate: selectedEmployee.startDate || '',
    tcNumber: selectedEmployee.tcNumber || '',
    role: selectedEmployee.role || '',
  });

  // Dil bilgisi editörünün çalışma listesi; kayıt formatına (string) çevrimi
  // aşağıdaki effect formData.languageInfo üzerinden yapar
  const [languageEntries, setLanguageEntries] = useState(() => parseLanguageInfo(selectedEmployee.languageInfo));
  const [activeSection, setActiveSection] = useState('personal');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, languageInfo: formatLanguageInfo(languageEntries) }));
  }, [languageEntries]);

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setDirty(true);
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setField(name, value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;

    // A company address typed here must be a valid one (other domains are kept as they were)
    if (isCompanyEmail(formData.eMail)) {
      const local = localPartOf(formData.eMail);
      if (local && !isValidLocalPart(local) && formData.eMail !== selectedEmployee.eMail) {
        const found = { eMail: t('employees.form.email.invalid') };
        setErrors(found);
        setActiveSection(summarizeErrors(found).firstSection);
        return;
      }
    }

    setSaving(true);
    try {
      // Check if email already exists (only if email is provided and changed)
      if (formData.eMail && formData.eMail.trim() !== '') {
        const trimmedEmail = formData.eMail.trim();

        // Only check if email has changed from the original
        if (trimmedEmail !== selectedEmployee.eMail) {
          const emailQuery = query(
            collection(db, 'Employees'),
            where('eMail', '==', trimmedEmail)
          );
          const emailSnapshot = await getDocs(emailQuery);

          if (!emailSnapshot.empty) {
            toast.error(t('employees.form.duplicateEmail'));
            setErrors({ eMail: t('employees.form.duplicateEmail') });
            setActiveSection('contact');
            return;
          }
        }
      }

      const employeeRef = doc(db, 'Employees', selectedEmployee.id);

      const { documentId, ...updateData } = formData;

      // Yetkilendirme alanını yalnızca yetkili kullanıcı değiştirebilir
      if (!canEditAuthorization) {
        delete updateData.authorization;
      }

      await updateDoc(employeeRef, updateData);
      toast.success(t('employees.form.updated'));
      await onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating employee:', error);
      toast.error(t('employees.form.updateError', { message: error.message }));
    } finally {
      setSaving(false);
    }
  };

  const label = (name) => t(`employees.form.fields.${name}`);
  const textProps = (name) => ({ id: fieldId(name), name, value: formData[name], onChange: handleInputChange, error: errors[name] });
  const choose = t('employees.form.choose');
  const primaryBadge = (addressKey) => (isPrimaryAddress(selectedEmployee, addressKey)
    ? <Badge tone="info" className="tw-text-[10px]">{t('employees.table.primaryAddress')}</Badge>
    : null);
  const roleOutOfList = formData.role && !EMPLOYEE_ROLE_OPTIONS.includes(formData.role);

  const renderSection = () => {
    if (activeSection === 'contact') {
      return (
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <CompanyEmailField
            id={fieldId('eMail')}
            label={label('eMail')}
            value={formData.eMail}
            onChange={(value) => setField('eMail', value)}
            error={errors.eMail}
            allowExternal
          />
          <Field label={label('phone')} htmlFor={fieldId('phone')}>
            <TextInput {...textProps('phone')} type="text" inputMode="tel" autoComplete="off" placeholder={t('employees.form.phoneExample')} />
          </Field>
          <Field label={label('emergencyPhone')} htmlFor={fieldId('emergencyPhone')}>
            <TextInput {...textProps('emergencyPhone')} type="text" inputMode="tel" autoComplete="off" placeholder={t('employees.form.emergencyExample')} />
          </Field>
          <Field label={label('address')} htmlFor={fieldId('address')} badge={primaryBadge(PRIMARY_ADDRESS_HOME)}>
            <TextArea {...textProps('address')} rows={3} />
          </Field>
          <Field label={label('workAddress')} htmlFor={fieldId('workAddress')} badge={primaryBadge(PRIMARY_ADDRESS_WORK)}>
            <TextArea {...textProps('workAddress')} rows={3} />
          </Field>
        </div>
      );
    }

    if (activeSection === 'employment') {
      return (
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <Field label={label('departman')} htmlFor={fieldId('departman')} hint={isLoadingDepartments ? t('employees.form.loadingDepartments') : undefined}>
            <SelectInput
              {...textProps('departman')}
              disabled={isLoadingDepartments}
              placeholder={choose}
              options={asOptions(departments)}
              current={isLoadingDepartments ? undefined : formData.departman}
            />
          </Field>
          <Field label={label('profession')} htmlFor={fieldId('profession')}>
            <SelectInput {...textProps('profession')} placeholder={choose} options={asOptions(PROFESSION_OPTIONS)} current={formData.profession} />
          </Field>
          {/* Rol, eğitim/SOP/form atamalarındaki grup adlarıyla birebir
              eşleşmek zorunda olduğundan serbest metin yerine listeden seçilir */}
          <Field
            label={label('role')}
            htmlFor={fieldId('role')}
            hint={roleOutOfList ? (
              <span className="tw-inline-flex tw-items-start tw-gap-1 tw-text-amber-700 dark:tw-text-amber-300">
                <LuTriangleAlert aria-hidden="true" className="tw-mt-px tw-h-3.5 tw-w-3.5 tw-shrink-0" />
                {t('employees.form.roleNotInList')}
              </span>
            ) : undefined}
          >
            <SelectInput {...textProps('role')} placeholder={choose} options={asOptions(EMPLOYEE_ROLE_OPTIONS)} current={formData.role} />
          </Field>
          <Field
            label={label('authorization')}
            htmlFor={fieldId('authorization')}
            badge={canEditAuthorization ? null : <LuLock aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-slate-400" />}
            hint={(isLoadingAuthorizationRoles && t('employees.form.loadingRoles')) || (!canEditAuthorization && t('employees.form.authorizationLocked')) || undefined}
          >
            <SelectInput
              {...textProps('authorization')}
              disabled={isLoadingAuthorizationRoles || !canEditAuthorization}
              title={canEditAuthorization ? undefined : t('employees.form.authorizationLocked')}
              placeholder={choose}
              options={asOptions(authorizationRoles)}
              current={isLoadingAuthorizationRoles ? undefined : formData.authorization}
            />
          </Field>
          <Field label={label('workType')} htmlFor={fieldId('workType')}>
            <SelectInput
              {...textProps('workType')}
              placeholder={choose}
              options={EMPLOYEE_COLUMNS.workType.options.map((option) => ({ value: option.value, label: optionLabel(t, option) }))}
              current={formData.workType}
            />
          </Field>
          <Field label={label('Active')}>
            <SegmentedControl
              options={[
                { value: '1', label: t('employees.status.active') },
                { value: '0', label: t('employees.status.inactive') },
              ]}
              value={String(formData.Active)}
              onChange={(value) => setField('Active', parseInt(value, 10))}
              layoutId={`${idPrefix}-status`}
              label={label('Active')}
              size="sm"
            />
          </Field>
          <Field label={label('startDate')} htmlFor={fieldId('startDate')}>
            <TextInput {...textProps('startDate')} type="date" />
          </Field>
          <Field label={label('annualPermit')} htmlFor={fieldId('annualPermit')}>
            <TextInput {...textProps('annualPermit')} type="number" min="0" step="1" inputMode="numeric" />
          </Field>
          <Field label={label('workCity')} htmlFor={fieldId('workCity')} hint={t('employees.form.workCityHint')}>
            <SelectInput {...textProps('workCity')} placeholder={choose} options={asOptions(PROVINCES_SORTED)} current={formData.workCity} />
          </Field>
        </div>
      );
    }

    if (activeSection === 'education') {
      return (
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <Field label={label('educationInfo')} htmlFor={fieldId('educationInfo')}>
            <SelectInput
              {...textProps('educationInfo')}
              placeholder={choose}
              options={EMPLOYEE_COLUMNS.educationInfo.options.map((option) => ({ value: option.value, label: optionLabel(t, option) }))}
              current={formData.educationInfo}
            />
          </Field>
          <Field label={label('languageInfo')} htmlFor={fieldId('languageInfo')} className="sm:tw-col-span-2">
            <LanguageEditor
              idPrefix={idPrefix}
              entries={languageEntries}
              onChange={(next) => {
                setLanguageEntries(next);
                setDirty(true);
              }}
            />
          </Field>
        </div>
      );
    }

    return (
      <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
        <Field label={label('ID')} htmlFor={fieldId('ID')}>
          <TextInput {...textProps('ID')} autoComplete="off" />
        </Field>
        <Field label={label('tcNumber')} htmlFor={fieldId('tcNumber')}>
          <TextInput {...textProps('tcNumber')} autoComplete="off" />
        </Field>
        <Field label={label('eName')} htmlFor={fieldId('eName')}>
          <TextInput {...textProps('eName')} autoComplete="off" />
        </Field>
        <Field label={label('eSurname')} htmlFor={fieldId('eSurname')}>
          <TextInput {...textProps('eSurname')} autoComplete="off" />
        </Field>
        <Field label={label('nationality')} htmlFor={fieldId('nationality')}>
          <SelectInput
            {...textProps('nationality')}
            placeholder={choose}
            options={NATIONALITY_OPTIONS.map((option) => ({ value: option.value, label: t(`employees.nationalities.${option.key}`) }))}
            current={formData.nationality}
          />
        </Field>
        <Field label={label('birthday')} htmlFor={fieldId('birthday')}>
          <TextInput {...textProps('birthday')} type="date" />
        </Field>
        <div className="sm:tw-col-span-2">
          <p className="tw-mb-1.5 tw-px-0.5 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{t('employees.form.flags')}</p>
          <div className="tw-grid tw-gap-2 sm:tw-grid-cols-3">
            {FLAG_FIELDS.map((name) => (
              <ToggleTile
                key={name}
                id={fieldId(name)}
                label={label(name)}
                // Stored as true/false or 'Yes'/'No'; a change is saved as 'Yes'/'No' (as before)
                checked={isYes(formData[name])}
                onChange={(on) => setField(name, on ? 'Yes' : 'No')}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const { counts } = summarizeErrors(errors);
  const fullName = `${selectedEmployee.eName || ''} ${selectedEmployee.eSurname || ''}`.trim();

  return (
    <EmployeeFormShell
      title={t('employees.form.editTitle')}
      subtitle={fullName || undefined}
      icon={(
        <GlassIcon gradient="tw-from-blue-400 tw-to-indigo-600 tw-shadow-blue-500/30" size="xs">
          <LuUserPen aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      formId={`${idPrefix}-form`}
      sections={getFormSections(t)}
      activeSection={activeSection}
      onSectionChange={setActiveSection}
      sectionErrors={counts}
      hasErrors={Object.values(errors).some(Boolean)}
      onSubmit={handleSubmit}
      saving={saving}
      submitLabel={t('employees.form.save')}
      dirty={dirty}
      onClose={onClose}
    >
      {renderSection()}
    </EmployeeFormShell>
  );
};

EditEmployeeModal.propTypes = {
  selectedEmployee: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default EditEmployeeModal;
